import jwtDecode from 'jwt-decode';
import {verify, sign} from 'jsonwebtoken';
import axios from './axios';
import {parseToJson} from '.';
import {
  LOGIN_TOKEN_TTL_REMAINING_THRESHOLD_IN_MINUTE,
  MAX_REFRESH_TOKEN_ATTEMPT,
} from '../constants';
import TokenService from '../services/User/token.service';
import {isFunction} from 'lodash';
import {getStorage, removeStorageList, setStorageList} from '.';
import {path} from '../routes/path';

const getTokenData = (
  accessToken: string | null,
  key: string,
  fallbackValue = null
) => {
  if (!accessToken) {
    return fallbackValue;
  }
  const decoded = jwtDecode<{[key: string]: any}>(accessToken);
  return decoded?.[key];
};

const isValidToken = (accessToken: string | null) => {
  const tokenExp = getTokenData(accessToken, 'exp');
  if (!tokenExp) {
    return false;
  }
  const currentTime = Date.now() / 1000;

  return tokenExp > currentTime;
};

//  const handleTokenExpired = (exp) => {
//   let expiredTimer;

//   window.clearTimeout(expiredTimer);
//   const currentTime = Date.now();
//   const timeLeft = exp * 1000 - currentTime;
//   console.log(timeLeft);
//   expiredTimer = window.setTimeout(() => {
//     console.log('expired');
//   }, timeLeft);
// };

const setSession = (accessToken: string | null) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    // This function below will handle when token is expired
    // const { exp } = jwtDecode(accessToken);
    // handleTokenExpired(exp);
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

/**
 * Decrypt AES-GCM encrypted token
 * @param {*} token
 * @returns
 */
const decryptAesGcmToken = async (token: string, fallbackValue = null) => {
  try {
    // Convert base64 strings to ArrayBuffer
    const keyData = Uint8Array.from(
      atob(process.env.REACT_APP_AES_GCM_DECRYPTION_KEY as string),
      c => c.charCodeAt(0)
    );
    const encryptedData = Uint8Array.from(atob(token), c => c.charCodeAt(0));

    // Extract nonce (first 12 bytes) and ciphertext with tag (rest)
    const nonce = encryptedData.slice(0, 12);
    const ciphertextWithTag = encryptedData.slice(12);

    // Import the key
    const key = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      {name: 'AES-GCM', length: keyData.byteLength * 8},
      false,
      ['decrypt']
    );

    // Decrypt the data
    const decrypted = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: nonce,
        additionalData: new Uint8Array(0),
        tagLength: 128,
      },
      key,
      ciphertextWithTag
    );

    // Convert the decrypted data to a string
    const decryptedValue = new TextDecoder().decode(decrypted);
    return parseToJson(decryptedValue);
  } catch (error) {
    return fallbackValue;
  }
};

interface CheckAndRefreshTokenIfReachThresholdProps {
  accessToken: string | null;
  refreshToken: string | null;
  onFailure: Function;
  onTokenExpNotValid: Function;
  onTokenExpired: Function;
  onRefreshTokenAttemptExceed: Function;
  onTokenReachThreshold?: Function;
  onRefreshTokenError: Function;
  onRefreshTokenSuccessully: Function;
  refreshTokenResponseCodeChecks?: {[key: string | number]: Function};
}

/**
 *
 * @param props
 * @returns
 */
const checkAndRefreshTokenConditionally = async (
  props: CheckAndRefreshTokenIfReachThresholdProps
) => {
  const {
    accessToken,
    refreshToken,
    onFailure,
    onTokenExpNotValid,
    onTokenExpired,
    onRefreshTokenAttemptExceed,
    onTokenReachThreshold,
    onRefreshTokenError,
    onRefreshTokenSuccessully,
    refreshTokenResponseCodeChecks,
  } = props;
  const forceLogOut = async () => {
    await removeStorageList([
      'accessToken',
      'refreshToken',
      'refreshTokenAttempt',
    ]);
    window.location.pathname = `/${path.AUTH}/${path.SIGN_IN}`;
  };
  const callbackOnFailure = async () => {
    const onFailureRes = await onFailure();
    if (onFailureRes !== false) {
      forceLogOut();
    }
  };
  if (!accessToken || !refreshToken) {
    await callbackOnFailure();
    return;
  }
  const tokenExp = getTokenData(accessToken, 'exp');
  const attempt = parseInt(getStorage('refreshTokenAttempt') || 0);
  // If token is expired, fall into fail state
  const isTokenExpExpired = !isValidToken(accessToken);
  if (!tokenExp) {
    const onTokenExpNotValidRes = await onTokenExpNotValid();
    if (onTokenExpNotValidRes !== false) {
      forceLogOut();
    }
  } else if (isTokenExpExpired) {
    const onTokenExpiredRes = await onTokenExpired();
    if (onTokenExpiredRes !== false) {
      forceLogOut();
    }
  } else {
    const isTokenReachTTLRemainingThreshold =
      tokenExp - Date.now() / 1000 <
      LOGIN_TOKEN_TTL_REMAINING_THRESHOLD_IN_MINUTE * 60;

    if (tokenExp && isTokenReachTTLRemainingThreshold) {
      if (attempt >= MAX_REFRESH_TOKEN_ATTEMPT) {
        const refreshTokenAttemptExceedRes =
          await onRefreshTokenAttemptExceed();
        if (refreshTokenAttemptExceedRes !== false) {
          forceLogOut();
        }
      } else {
        if (onTokenReachThreshold) {
          await onTokenReachThreshold();
        }
        await new TokenService()
          .getRefeshToken({
            accessToken: accessToken,
            refreshToken: refreshToken,
          })
          .then(async res => {
            const matchedCodeCheckFunction =
              refreshTokenResponseCodeChecks?.[res?.code];
            if (
              matchedCodeCheckFunction &&
              isFunction(matchedCodeCheckFunction)
            ) {
              await matchedCodeCheckFunction();
            } else {
              const {token: newAccessToken, refreshToken: newRefreshToken} =
                res?.data;
              if (!newAccessToken || !newRefreshToken) {
                await callbackOnFailure();
              } else {
                await setStorageList([
                  {
                    key: 'accessToken',
                    value: newAccessToken,
                  },
                  {
                    key: 'refreshToken',
                    value: newRefreshToken,
                  },
                  {
                    key: 'refreshTokenAttempt',
                    value: attempt + 1,
                  },
                ]);
                const onRefreshTokenSuccessfullyRes =
                  await onRefreshTokenSuccessully(res);
                if (onRefreshTokenSuccessfullyRes !== false) {
                  setTimeout(() => {
                    window.location.reload();
                  }, 200);
                }
              }
            }
          })
          .catch(async () => {
            const onRefreshTokenErrorRes = await onRefreshTokenError();
            if (onRefreshTokenErrorRes !== false) {
              forceLogOut();
            }
          });
      }
    }
  }
};

export {
  verify,
  sign,
  getTokenData,
  isValidToken,
  setSession,
  checkAndRefreshTokenConditionally,
  decryptAesGcmToken,
};
